import React, { useEffect } from 'react';
import { useTranslateFn } from 'utils/useTranslateFn';
import Loader from 'common/components/Loader';
import type { IAssemblyGroup, IAssemblyGroupItem } from 'containers/AssemblyGroupStorage/types';
import styles from './AssemblyGroupView.module.scss';

interface IAssemblyGroupViewProps {
  group: IAssemblyGroup | undefined;
  groupKey: string | null;
  onClose: () => void;
  onDelete: (itemUuid: string) => void;
  isDeleting: boolean;
}

const AssemblyCard = ({
  item,
  onDelete,
  isCompleted = false,
  isDeleting = false,
}: {
  item: IAssemblyGroupItem;
  onDelete: (itemUuid: string) => void;
  isCompleted?: boolean;
  isDeleting?: boolean;
}) => {
  const s = item.summaryData;

  const lengthDisplay = s.lengthType === 'feet'
    ? `${s.lengthInFeet} feet`
    : `${s.lengthInInches} inches`;

  return (
    <div className={styles.card}>
      <div className={styles.cardInfo}>
        <p>
          <strong>Hose:</strong>
          {' '}
          {s.hose?.title || '—'}
        </p>
        <p>
          <strong>Fitting 1:</strong>
          {' '}
          {s.fitting1?.title || '—'}
        </p>
        <p>
          <strong>Fitting 2:</strong>
          {' '}
          {s.fitting2?.title || '—'}
        </p>
        <p>
          <strong>Overall Assembly Length:</strong>
          {' '}
          {lengthDisplay}
        </p>
      </div>
      <div className={styles.cardActions}>
        <button
          type="button"
          className={styles.deleteButton}
          onClick={() => onDelete(item.uuid)}
          disabled={isCompleted || isDeleting}
          aria-label="Delete"
        >
          <svg
            width="16"
            height="18"
            viewBox="0 0 16 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 4.5H15M6 8V13.5M10 8V13.5M2 4.5L3 15.5C3 16.0523 3.44772 16.5 4 16.5H12C12.5523 16.5 13 16.0523 13 15.5L14 4.5M5.5 4.5V2.5C5.5 1.94772 5.94772 1.5 6.5 1.5H9.5C10.0523 1.5 10.5 1.94772 10.5 2.5V4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export const AssemblyGroupView = ({
  group,
  groupKey,
  onClose,
  onDelete,
  isDeleting,
}: IAssemblyGroupViewProps) => {
  const t = useTranslateFn<string>();

  // Lock body scroll when overlay is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!group || !groupKey) return null;

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div className={styles.backdrop} onClick={isDeleting ? undefined : onClose} />
      {isDeleting && <div className={styles.overlayLoader}><Loader /></div>}
      <div className={styles.overlay}>
        <div className={styles.header}>
          <h2>
            {t('texts.assemblyGroup.title')}
            {' '}
            {`(${group.itemsCount} Items)`}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            disabled={isDeleting}
            aria-label="Close"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1L13 13M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className={styles.cardList}>
          {group.items.map((item) => (
            <AssemblyCard
              key={item.uuid}
              item={item}
              onDelete={onDelete}
              isCompleted={group.status === 'completed'}
              isDeleting={isDeleting}
            />
          ))}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.footerButton}
            onClick={onClose}
            disabled={isDeleting}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};

export default AssemblyGroupView;
